import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

interface CallHandlerSchedule extends BusinessHourRange {
  isActiveMonday: boolean;
  isActiveTuesday: boolean;
  isActiveWednesday: boolean;
  isActiveThursday: boolean;
  isActiveFriday: boolean;
  isActiveSaturday: boolean;
  isActiveSunday: boolean;
}

interface CallHandlerHoliday extends BusinessHourRange {
  name: string;
  startDate: Date;
  endDate: Date;
}

interface BusinessHourRange {
  startTime: number;
  endTime: number;
}

class BusinessHourSchedule {
  monday: BusinessHourRange[] = [];
  tuesday: BusinessHourRange[] = [];
  wednesday: BusinessHourRange[] = [];
  thursday: BusinessHourRange[] = [];
  friday: BusinessHourRange[] = [];
  saturday: BusinessHourRange[] = [];
  sunday: BusinessHourRange[] = [];
}

@Injectable()
export class MigrationsCiscoAutoAttendantsScheduleService {
  private _translateService = inject(TranslateService);

  determineBusinessHours(schedules: { [index: string]: string }[]): string {
    const parsedSchedules = schedules
      .map((schedule) => this._parseSchedule(schedule))
      .sort((a, b) => a.startTime - b.startTime);
    const businessHourSchedule = new BusinessHourSchedule();

    parsedSchedules.forEach((schedule) => {
      const range: BusinessHourRange = { startTime: schedule.startTime, endTime: schedule.endTime };
      if (schedule.isActiveMonday) {
        this._addRangeToSchedule(range, businessHourSchedule.monday);
      }
      if (schedule.isActiveTuesday) {
        this._addRangeToSchedule(range, businessHourSchedule.tuesday);
      }
      if (schedule.isActiveWednesday) {
        this._addRangeToSchedule(range, businessHourSchedule.wednesday);
      }
      if (schedule.isActiveThursday) {
        this._addRangeToSchedule(range, businessHourSchedule.thursday);
      }
      if (schedule.isActiveFriday) {
        this._addRangeToSchedule(range, businessHourSchedule.friday);
      }
      if (schedule.isActiveSaturday) {
        this._addRangeToSchedule(range, businessHourSchedule.saturday);
      }
      if (schedule.isActiveSunday) {
        this._addRangeToSchedule(range, businessHourSchedule.sunday);
      }
    });

    const html = Object.entries(businessHourSchedule)
      .map(([key, value]) => this._generateHumanReadableHtmlForDay(key, value))
      .join('');
    return `<div class="d-grid gap-2">${html}</div>`;
  }

  determineHolidayHours(holidays: { [index: string]: string }[]): string {
    if (!holidays.length) {
      return this._translateService.instant(
        'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.off_hours.none',
      );
    }
    const parsedHolidays = holidays
      .map((holiday) => this._parseHolidaySchedule(holiday))
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

    const html = parsedHolidays.map((holiday) => this._generateHumanReadableHtmlForHoliday(holiday)).join('');
    return `<div class="d-grid gap-2">${html}</div>`;
  }

  private _parseSchedule(schedule: { [index: string]: string }): CallHandlerSchedule {
    return {
      isActiveMonday: schedule.isActiveMonday === 'true',
      isActiveTuesday: schedule.isActiveTuesday === 'true',
      isActiveWednesday: schedule.isActiveWednesday === 'true',
      isActiveThursday: schedule.isActiveThursday === 'true',
      isActiveFriday: schedule.isActiveFriday === 'true',
      isActiveSaturday: schedule.isActiveSaturday === 'true',
      isActiveSunday: schedule.isActiveSunday === 'true',
      ...this._parseStartAndEndTimes(schedule),
    };
  }

  private _parseHolidaySchedule(holiday: { [index: string]: string }): CallHandlerHoliday {
    return {
      name: holiday.subject,
      startDate: new Date(holiday.startDate?.slice(0, 10)),
      endDate: new Date(holiday.endDate?.slice(0, 10)),
      ...this._parseStartAndEndTimes(holiday),
    };
  }

  private _parseStartAndEndTimes(schedule: { [index: string]: string }): BusinessHourRange {
    const startTime = Number(schedule.startTime);
    const endTime = Number(schedule.endTime);
    return {
      startTime: isNaN(startTime) ? 0 : startTime,
      endTime: schedule.endOfDay === 'true' || isNaN(endTime) || schedule.endTime === '0' ? 1440 : endTime,
    };
  }

  private _addRangeToSchedule(range: BusinessHourRange, dayRanges: BusinessHourRange[]): void {
    const lastRange = dayRanges[dayRanges.length - 1];
    if (lastRange && range.startTime <= lastRange.endTime) {
      lastRange.endTime = Math.max(lastRange.endTime, range.endTime);
    } else {
      dayRanges.push({ ...range });
    }
  }

  private _generateHumanReadableHtmlForDay(dayName: string, ranges: BusinessHourRange[]): string {
    const dayHtml = this._translateService.instant(
      `tkey;migrations.cisco.auto_attendants_report.explorer.greetings.standard.schedule.${dayName}`,
    );

    let hoursHtml: string;
    if (!ranges.length) {
      hoursHtml = this._translateService.instant(
        `tkey;migrations.cisco.auto_attendants_report.explorer.greetings.standard.schedule.closed`,
      );
    } else if (ranges[0].endTime - ranges[0].startTime === 1440) {
      hoursHtml = this._translateService.instant(
        `tkey;migrations.cisco.auto_attendants_report.explorer.greetings.standard.schedule.open`,
      );
    } else {
      hoursHtml = ranges.map((range) => this._generateHumanReadableBusinessHours(range)).join(', ');
    }

    return `<div>${dayHtml}\n${hoursHtml}</div>`;
  }

  private _generateHumanReadableBusinessHours(range: BusinessHourRange): string {
    return this._translateService.instant(
      'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.standard.schedule.range',
      {
        startTime: this._generateHumanReadableTime(range.startTime),
        endTime: this._generateHumanReadableTime(range.endTime),
      },
    );
  }

  private _generateHumanReadableTime(timeInMinutes: number): string {
    let hour = Math.floor(timeInMinutes / 60) % 12;
    if (hour === 0) {
      hour = 12;
    }
    let minutes: string | number = timeInMinutes % 60;
    if (minutes < 10) {
      minutes = `0${minutes}`;
    }
    const amPm = timeInMinutes < 720 || timeInMinutes === 1440 ? 'AM' : 'PM';

    return `${hour}:${minutes} ${amPm}`;
  }

  private _generateHumanReadableHtmlForHoliday(holiday: CallHandlerHoliday): string {
    const holidayName = this._translateService.instant(
      'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.off_hours.holiday_name',
      { name: holiday.name },
    );
    const dateRange = this._generateHumanReadableDateRange(holiday.startDate, holiday.endDate);
    const timeRange = this._generateHumanReadableHolidayTimeRange(holiday.startTime, holiday.endTime);

    return `<div>${holidayName}\n${dateRange}\n${timeRange}</div>`;
  }

  private _generateHumanReadableHolidayTimeRange(startTime: number, endTime: number): string {
    if (endTime - startTime === 1440) {
      return this._translateService.instant(
        'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.off_hours.closed_all_day',
      );
    }
    return this._translateService.instant(
      'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.off_hours.closed_range',
      {
        startTime: this._generateHumanReadableTime(startTime),
        endTime: this._generateHumanReadableTime(endTime),
      },
    );
  }

  private _generateHumanReadableDateRange(startDate: Date, endDate: Date): string {
    if (startDate.toISOString() === endDate.toISOString()) {
      return this._generateHumanReadableDate(startDate);
    }
    return this._translateService.instant(
      'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.off_hours.date_range',
      { startDate: this._generateHumanReadableDate(startDate), endDate: this._generateHumanReadableDate(endDate) },
    );
  }

  private _generateHumanReadableDate(date: Date): string {
    const formatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'long', timeZone: 'UTC' });
    return formatter.format(date);
  }
}
